import { Injectable, inject } from '@angular/core';
import { ApiException } from 'api-clients/ai-api-client';
import { DiscussionClient, DiscussionModel } from 'api-clients/discussion-api-client';
import { EvaSourceLink, EvaSourceMetadata } from 'src/app/eva-sidebar/shared/models/eva-source-link.model';
import { EvaSourceLabel, EvaSourceName, EvaSourceType } from 'src/app/eva-sidebar/shared/models/eva-source.enum';
import { EvaSharedApisService } from 'src/app/eva-sidebar/shared/services/eva-shared-apis.service';
import { BannerService } from 'src/shared/banners/banner.service';

/**
 * Owns everything the EVA source popovers need: the per-entity metadata fetch/cache and the
 * type-specific text/status resolution. Kept out of EvaQuestionAnswerService (chat orchestration)
 * and the text-formatter component (view/render) so each has a single responsibility.
 */
@Injectable()
export class EvaSourcePopoverService {
  readonly #evaSharedApisService = inject(EvaSharedApisService);
  readonly #discussionClient = inject(DiscussionClient);
  readonly #bannerService = inject(BannerService);

  // Source-popover metadata cache, shared across all message bubbles. Only successful responses are
  // cached (see #getDiscussion), so a failed request is never stored and just retries.
  // Applicant and policy metadata are cached by EvaSharedApisService, which shares them with the rest
  // of the sidebar — the suggested prompts read the same policy preview this popover does.
  readonly #discussionCache = new Map<number, DiscussionModel>();

  /** Status text shown in the source popover when an entity can't be loaded. Each builder takes the EvaSourceLabel for the source type. */
  evaSourceUnavailableMessage = {
    deleted: (label: EvaSourceLabel) => this.#popOverMessage`This ${label} has been deleted.`,
    notFound: (label: EvaSourceLabel) => this.#popOverMessage`This ${label} is no longer available.`,
    unauthorized: (label: EvaSourceLabel) => this.#popOverMessage`You don't have permission to view this ${label}.`,
  } as const;

  /** Button label shown in the source popover for a valid discussion (opens it in the workspace panel). */
  evaSourceButtonLabel = {
    discussion: 'Open',
  } as const;

  /** Placeholder shown in the source popover while its metadata is loading. */
  readonly loadingMessage = 'Loading...';

  /** Template-literal tag for the source-popover unavailable messages —
* interleaves any number of labels with the literal parts (seam for future formatting/i18n). */
  #popOverMessage = (strings: TemplateStringsArray, ...labels: EvaSourceLabel[]): string =>
    strings.reduce((previousValue, currentValue, index) => `${previousValue}${currentValue}${labels[index] ?? ''}`, '');
  /**
   * Returns the metadata shown in the source popover, resolved per source type.
   * If the entity is unavailable (deleted / not found / unauthorized) a quiet status message is shown
   * instead of the link; other failures (5xx/network) still show an error banner and fall back to link.text.
   */
  async fetchSourceMetadata(link: EvaSourceLink, signal?: AbortSignal): Promise<EvaSourceMetadata | undefined> {
    try {
      // Discussion: verify existence + deleted flag, then show the "Open" button (opens the workspace panel).
      if (link.type === EvaSourceType.Discussion && link.discussionId != null) {
        return await this.#resolveDiscussionMetadata(link, link.discussionId, signal);
      }
      const sourceText = await this.#resolveSourceText(link, signal);
      if (sourceText === null) {
        // A deleted applicant resolves to null (HTTP 204) rather than throwing.
        return { message: this.evaSourceUnavailableMessage.notFound(this.#sourceLabel(link.type)) };
      }
      return { sourceText: sourceText || link.text };
    } catch (error) {
      // A newer open aborted this fetch — bail with no banner.
      if (error instanceof DOMException && error.name === 'AbortError') {
        return undefined;
      }
      const message = this.#unavailableMessage(link.type, error);
      if (message) {
        return { message };
      }
      // null → keep the banner + raw-text fallback.
      this.#bannerService.addError(error, 'Failed to Load Source Details');
      return { sourceText: link.text };
    }
  }

  /** Discussion source: null → not found, deleted flag → deleted message, otherwise the "Open" button (with applicant name when available). */
  async #resolveDiscussionMetadata(link: EvaSourceLink, discussionId: number, signal?: AbortSignal): Promise<EvaSourceMetadata> {
    const discussion = await this.#getDiscussion(discussionId, signal);
    if (!discussion) {
      // A removed discussion resolves to null (HTTP 204 / empty body) rather than throwing.
      return { message: this.evaSourceUnavailableMessage.notFound(this.#sourceLabel(link.type)) };
    }
    if (discussion.deleted) {
      return { message: this.evaSourceUnavailableMessage.deleted(this.#sourceLabel(link.type)) };
    }
    const applicantName = await this.#resolveDiscussionApplicantName(link.applicantId, signal);
    if (applicantName) {
      link.sourceName = `${EvaSourceName.Discussion} - ${applicantName}`;
    }
    return { sourceText: this.evaSourceButtonLabel.discussion };
  }

  /** Returns the discussion for a source popover, fetching once per discussionId and reusing the cached data. */
  async #getDiscussion(discussionId: number, signal?: AbortSignal): Promise<DiscussionModel | null> {
    const cachedDiscussionDetails = this.#discussionCache.get(discussionId);
    if (cachedDiscussionDetails) {
      return cachedDiscussionDetails;
    }
    // getDiscussion2 is typed non-null but resolves null (HTTP 204 / empty body) for a removed discussion.
    // Cache only a non-null response — a null or rejected request is never stored, so it re-probes next hover.
    const discussion = await this.#discussionClient.getDiscussion2(discussionId, signal) as DiscussionModel | null;
    if (discussion) {
      this.#discussionCache.set(discussionId, discussion);
    }
    return discussion;
  }

  /** User-facing noun per source type, used in the unavailable messages. */
  #sourceLabel(type: EvaSourceType): EvaSourceLabel {
    switch (type) {
      case EvaSourceType.Policy:
        return EvaSourceLabel.Policy;
      case EvaSourceType.Discussion:
        return EvaSourceLabel.Discussion;
      default:
        return EvaSourceLabel.Applicant;
    }
  }

  /** Maps a 400/404 (not found) or 401/403 (unauthorized) API failure to a popover status message; */
  #unavailableMessage(type: EvaSourceType, error: unknown): string | null {
    if (!ApiException.isApiException(error)) {
      return null;
    }
    const label = this.#sourceLabel(type);
    if (error.status === 400 || error.status === 404) {
      return this.evaSourceUnavailableMessage.notFound(label);
    }
    if (error.status === 401 || error.status === 403) {
      return this.evaSourceUnavailableMessage.unauthorized(label);
    }
    return null;
  }

  /** Applicant → applicant name (null if the applicant was deleted);
   * Policy → policy number; otherwise the inline text. Metadata is cached in this service. */
  async #resolveSourceText(link: EvaSourceLink, signal?: AbortSignal): Promise<string | null> {
    if (link.type === EvaSourceType.Applicant && link.applicantId != null) {
      return this.#resolveApplicantSourceText(link.applicantId, signal);
    }
    if (link.type === EvaSourceType.Policy && link.policyId != null) {
      const preview = await this.#evaSharedApisService.getPolicyPreview(link.policyId, signal);
      if (!preview) {
        // A deleted policy resolves to null (empty body) — mirror the applicant path so the not-found message shows.
        return null;
      }
      return preview.policyNumber ? `Policy Number: ${preview.policyNumber}` : '';
    }
    return link.text;
  }

  /** Applicant name for the discussion button label; null (→ name-less label) if unavailable — never blocks opening a live discussion. */
  async #resolveDiscussionApplicantName(applicantId?: number, signal?: AbortSignal): Promise<string | null> {
    if (applicantId == null) {
      return null;
    }
    try {
      return await this.#resolveApplicantSourceText(applicantId, signal);
    } catch {
      return null;
    }
  }

  /** Applicant display text: the applicant name. Null if the applicant was deleted (HTTP 204). */
  async #resolveApplicantSourceText(applicantId: number, signal?: AbortSignal): Promise<string | null> {
    const applicant = await this.#evaSharedApisService.getApplicant(applicantId, signal);
    if (!applicant) {
      return null;
    }
    return applicant.isCommercial
      ? applicant.businessName ?? ''
      : [applicant.firstName, applicant.lastName].filter(Boolean).join(' ');
  }
}
